import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  Card,
  Divider,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import api from "../../utils/api";

const AddResidentVehicleForm = ({ propertyId, memberId, onSuccess }) => {
  const [formData, setFormData] = useState({
    property_id: propertyId || "",
    member_id: memberId || "",
    license_plate: "",
    state: "",
    make: "",
    model: "",
    color: "",
    year: "",
    permit_number: "",
    sticker_number: "",
    permit_type: "resident",
    status: "active",
    start_date: "",
    end_date: "",
    is_primary: false,
    is_blacklisted: false,
    notes: "",
    rfid_tag: "",
    qr_code: "",
    created_by: localStorage.getItem("userId") || null,
  });

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      property_id: propertyId || "",
      member_id: memberId || "",
    }));
  }, [propertyId, memberId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "is_primary" || name === "is_blacklisted") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "true",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData((prev) => ({
      ...prev,
      license_plate: "",
      state: "",
      make: "",
      model: "",
      color: "",
      year: "",
      permit_number: "",
      sticker_number: "",
      permit_type: "resident",
      status: "active",
      start_date: "",
      end_date: "",
      is_primary: false,
      is_blacklisted: false,
      notes: "",
      rfid_tag: "",
      qr_code: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.property_id || !formData.license_plate.trim()) {
      setSnackbar({
        open: true,
        message: "Property and license plate are required.",
        severity: "warning",
      });
      return;
    }

    try {
      setSaving(true);

      await api.post(`/resident-vehicles`, {
        ...formData,
        year: formData.year ? Number(formData.year) : null,
      });

      setSnackbar({
        open: true,
        message: "Vehicle added successfully.",
        severity: "success",
      });

      resetForm();

      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error("Failed to add resident vehicle:", err);
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message || "Failed to add resident vehicle.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: { xs: 1.5, sm: 2, md: 2.5 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  return (
    <>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <DirectionsCarIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Add Resident Vehicle
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
            Register a vehicle for this member/property, including permit,
            sticker, access, and status information.
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`Property: ${propertyId || "N/A"}`}
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                backgroundColor: "rgba(47,99,173,0.08)",
                color: "#2f63ad",
              }}
            />
            {memberId ? (
              <Chip
                size="small"
                label={`Member ID: ${memberId}`}
                sx={{
                  borderRadius: "999px",
                  fontWeight: 600,
                  backgroundColor: "rgba(47,99,173,0.06)",
                  color: "#4b5b70",
                }}
              />
            ) : null}
          </Box>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Card sx={sectionCardSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#223142" }}
                >
                  Vehicle Information
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
                Enter the plate, vehicle details, permit info, and optional
                gate access identifiers.
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="License Plate"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Property ID"
                    value={propertyId || ""}
                    InputProps={{ readOnly: true }}
                    sx={readonlyFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Make"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    label="Color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    label="Year"
                    name="year"
                    type="number"
                    value={formData.year}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="revoked">Revoked</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Permit Type"
                    name="permit_type"
                    value={formData.permit_type}
                    onChange={handleChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="resident">Resident</MenuItem>
                    <MenuItem value="guest">Guest</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                    <MenuItem value="contractor">Contractor</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Permit Number"
                    name="permit_number"
                    value={formData.permit_number}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Sticker Number"
                    name="sticker_number"
                    value={formData.sticker_number}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="RFID Tag"
                    name="rfid_tag"
                    value={formData.rfid_tag}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="QR Code"
                    name="qr_code"
                    value={formData.qr_code}
                    onChange={handleChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="start_date"
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="end_date"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Primary Vehicle"
                    name="is_primary"
                    value={String(formData.is_primary)}
                    onChange={handleChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Blacklisted"
                    name="is_blacklisted"
                    value={String(formData.is_blacklisted)}
                    onChange={handleChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ borderColor: "rgba(47,99,173,0.08)" }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    sx={textFieldSx}
                    placeholder="Add internal notes, parking notes, or special access details..."
                  />
                </Grid>
              </Grid>
            </Card>

            <Divider sx={{ my: 3, borderColor: "rgba(47,99,173,0.08)" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={resetForm}
                disabled={saving}
                sx={secondaryButtonSx}
              >
                Reset
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={primaryButtonSx}
              >
                {saving ? "Saving..." : "Save Vehicle"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddResidentVehicleForm;